import React, { FC, useEffect, memo } from "react";
import {
  FormBasicsDto,
  FormChildQuestionDto,
  FormQuestionDto,
  FormSectionDto,
  QuestionComponentType,
} from "@green0meter/api-client";

import NumberInput from "./components/NumberInput";
import Radio from "./components/Radio";
import Checkbox from "./components/Checkbox";
import Select from "./components/Select";
import Slider from "./components/Slider";
import MultiInput from "./components/MultiInput";
import TableInput from "./components/TableInput";
import ShortText from "./components/ShortText";
import Files from "./components/Files";
import DatePicker from "./components/DatePicker";
import Email from "./components/Email";
import RelevantYear from "./components/RelevantYear";
import RichTextBox from "./components/RichTextBox";

import { useDraftReturn } from "@hooks/useDraftField";
import { Grid } from "@components/base";
import { useUserStore } from "@store/user";
import { useCreateUpdateDraftData } from "@api";
import { useMultistepFormStore } from "@components/common/MultistepForm";

interface Input {
  formBasics?: FormBasicsDto;
  section?: FormSectionDto;
  question: FormQuestionDto | FormChildQuestionDto;
  draftField?: useDraftReturn | null;
}

const Input: FC<Input> = ({ formBasics, section, question, draftField }) => {
  const {
    formSettings: {
      formBasics: { id },
    },
  } = useMultistepFormStore();
  const { user } = useUserStore();
  const { createUpdateDraftDataEnabled } = useCreateUpdateDraftData(user, id);

  const disabled =
    formBasics?.isLocked || !createUpdateDraftDataEnabled || draftField?.isLocked;

  useEffect(() => {
    return () => draftField?.handleBlur();
  }, []);

  switch (question.componentType) {
    case QuestionComponentType.Number:
      return (
        <Grid container spacing={2}>
          <Grid size={{ md: 6, xs: 12 }}>
            <NumberInput
              question={question}
              disabled={disabled}
              draftField={draftField}
            />
          </Grid>
          {(question as FormQuestionDto).showRelevantYear && (
            <Grid size={{ md: 6, xs: 12 }}>
              <RelevantYear question={question as FormQuestionDto} />
            </Grid>
          )}
        </Grid>
      );
    case QuestionComponentType.Radio:
      return <Radio question={question} disabled={disabled} />;
    case QuestionComponentType.Select:
      return (
        <Grid container>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select question={question} disabled={disabled} />
          </Grid>
        </Grid>
      );
    case QuestionComponentType.Slider:
      return <Slider question={question} disabled={disabled} />;
    case QuestionComponentType.MultiInput:
      return (
        <MultiInput question={question as FormQuestionDto} disabled={disabled} />
      );
    case QuestionComponentType.Table:
      return (
        <TableInput
          question={question as FormQuestionDto}
          section={section}
          formBasics={formBasics}
          disabled={disabled}
          draftField={draftField}
        />
      );
    // TODO: Implement the rest of the components
    case QuestionComponentType.ShortText:
      return (
        <Grid container>
          <Grid size={{ md: 6, xs: 12 }}>
            <ShortText
              question={question}
              disabled={disabled}
              draftField={draftField}
            />
          </Grid>
        </Grid>
      );
    case QuestionComponentType.Files:
      return section ? <Files question={question} disabled={disabled} formId={formBasics?.id ?? id} /> : null;
    case QuestionComponentType.DatePicker:
      return (
        <Grid container>
          <Grid size={{ md: 6, xs: 12 }}>
            <DatePicker question={question} disabled={disabled} />
          </Grid>
        </Grid>
      );
    case QuestionComponentType.Checkbox:
      return <Checkbox question={question} disabled={disabled} />;
    case QuestionComponentType.Email:
      return (
        <Email question={question} disabled={disabled} draftField={draftField} />
      );
    case QuestionComponentType.RichTextBox:
      return (
        <RichTextBox
          question={question}
          disabled={disabled}
          draftField={draftField}
        />
      );

    default:
      return null;
  }
};

export default memo(Input);
